<template>
  <div>
    <highcharts
      ref="IncChart"
      v-show="showMainChart"
      :options="incomeChartOptions"
      @showDetail="toggleDisplayChart"
    ></highcharts>
    <highcharts
      ref="DetailChart"
      v-show="showDetailChart"
      :options="detailChartOptions"
    ></highcharts>
  </div>
</template>

<script lang="ts">
    import { defineComponent, ref } from "vue";
    import Highcharts from "highcharts";
    import { Chart } from "highcharts-vue";
    import TooltipFormatter from "@/guiide/ts/tooltipFormatter";
    import ChartSeries from "@/guiide/ts/chartSeries";
    import "highcharts/modules/accessibility";

    export default defineComponent({
      name: "IncomeChart",
      components: {
        highcharts: Chart
      },
      setup() {
        const IncChart = ref(null);
        const DetailChart = ref(null);

        return {
          IncChart,
          DetailChart
        };
      },
      mounted() {
        this.DetailChart.chart.renderer
          .button("Back", 16, 0, () => this.toggleDisplayChart(), {
            height: 10,
            width: 28
          })
          .add();
      },
      data() {
        return {
          showMainChart: true,
          detailChartYear: 1
        };
      },
      beforeUpdate() {
        this.IncChart.chart.reflow();
        this.DetailChart.chart.reflow();
      },
      methods: {
        toggleDisplayChart() {
          this.showMainChart = !this.showMainChart;
        },
        configureForDetailChart(retirementYear: number) {
          this.detailChartYear = retirementYear;
          this.toggleDisplayChart();
        }
      },
      computed: {
        showIncomeChart(): boolean {
          return this.showMainChart;
        },
        showDetailChart(): boolean {
          return !this.showMainChart;
        },
        getIncomeChartData() {
          return this.$store.state.incomeChartData;
        },
        getDetailChartData() {
          const detailData = this.$store.state.annualIncomeData
            .filter(aid => aid.Year == this.detailChartYear)
            .map(d => ({
              name: d.Name,
              y: d.Value,
              color: d.Color
            }));

          return [
            {
              colorByPoint: true,
              data: detailData
            }
          ];
        },
        incomeChartOptions() {
          return {
            accessibility: {
                enabled: true,
                description: 'An income plan chart displaying the distribution of funds across 30 years of retirement with tax and any expected shortfalls.',
                keyboardNavigation: {
                    enabled: true,
                },
                tooltip: {
                    enabled: true,
                },
                announceNewData: {
                    enabled: true,
                    minAnnounceInterval: 2000
                },
                pointDescriptionFormatter: function (point) {
                    return `Year ${point.category}, value ${point.y} thousand pounds.`;
                }
            },
            chart: {
              backgroundColor: "white",
              borderColor: "",
              borderWidth: 1,
              borderRadius: 3,
              zoomType: "x",
              panning: true,
              panKey: "shift",
              type: "column",
              height: 380
            },
            title: {
              text: "Income in Each Year",
              style: {
                color: "#5c5b5b",
                fontWeight: "600",
                fontSize: "13px"
              }
            },
            xAxis: {
              title: {
                text: "Retirement Year",
                style: {
                  color: "#5c5b5b",
                  fontSize: "12px"
                }
              },
              accessibility: {
                  description: 'Each year of retirement (up to 30).'
              },
              allowDecimals: false,
              tickInterval: 1,
              tickLength: 0
            },
            yAxis: {
              title: {
                text: "£",
                rotation: 0,
                style: {
                  color: "Black",
                  fontWeight: "500",
                  fontSize: "14px"
                },
                accessibility: {
                    description: 'Cash amount (in thousands) allocated to each year.'
                }
              },
              labels: {
                formatter: function() {
                  return (
                    "£" +
                    Highcharts.numberFormat(this.value / 1000, 0, ".", ",") +
                    "k"
                  );
                }
              },
              stackLabels: {
                enabled: false,
                style: {
                  fontWeight: "bold",
                  color: "gray"
                }
              }
            },
            legend: {
              enabled: true,
              verticalAlign: "bottom",
              floating: false,
              backgroundColor: "white",
              borderColor: "",
              borderWidth: 0,
              borderRadius: 0,
              shadow: false,
              itemMarginBottom: 6,
              itemStyle: {
                color: "Grey",
                fontWeight: "lighter",
                fontSize: "12px"
              }
            },
            tooltip: {
              borderColor: "DimGrey",
              backgroundColor: "#EBF4FA",
              borderWidth: 0,
              borderRadius: 10,
              shadow: false,
              animation: true,
              formatter: function() {
                const allSeries = this.series.chart.series;
                const allChartSeries = new Array<ChartSeries>();
                allSeries.forEach(s => {
                  const yData = s.data.map(d => d.y);
                  const chartSeries = new ChartSeries(s.name, yData);
                  allChartSeries.push(chartSeries);
                });
                return TooltipFormatter.createNew(this.x, allChartSeries);
              },
              positioner: function(labelWidth, labelHeight, point) {
                return {
                  x: Math.max(point.plotX - labelWidth + 45, 10),
                  y: 0
                };
              }
            },
            plotOptions: {
              line: {
                pointStart: 1
              },
              column: {
                pointPadding: -0.1,
                borderWidth: 1,
                shadow: false,
                borderRadius: 0,
                stacking: "normal",
                dataLabels: {
                  enabled: false,
                  color: "black"
                },
                pointStart: 1,
                cursor: "pointer",
                point: {
                  events: {
                    click: ({ point }) => {
                      this.configureForDetailChart(point.category);
                    }
                  }
                }
              },
              series: this.getIncomeChartData
            },
            series: this.getIncomeChartData
          };
        },
        detailChartOptions() {
          return {
            accessibility: {
                enabled: true,
                description: 'A drilldown pie chart showing a breakdown of all income sources for the given year.',
                keyboardNavigation: {
                    enabled: true,
                },
                announceNewData: {
                    enabled: true,
                    minAnnounceInterval: 2000
                }
            },
            chart: {
              plotBackgroundColor: null,
              plotBorderWidth: null,
              plotShadow: false,
              type: "pie",
              height: 380
            },
            title: {
              text: "Income Source",
              style: {
                color: "#5c5b5b",
                fontWeight: "600",
                fontSize: "13px"
              }
            },
            tooltip: {
              enabled: false
            },
            plotOptions: {
              pie: {
                allowPointSelect: false,
                cursor: "pointer",
                dataLabels: {
                  enabled: true,
                  style: {
                    fontSize: "0.9em",
                    fontWeight: "lighter"
                  },
                  formatter: function() {
                    return (
                      this.point.name +
                      ":<br/>£" +
                      Highcharts.numberFormat(this.y, 0, ".", ",")
                    );
                  }
                }
              }
            },
            series: this.getDetailChartData
          };
        }
      }
    });
</script>

<style>
    .highcharts-container {
      width: 100% !important;
    }
</style>
